import Layout from 'components/layouts/Layout';
import NodeNewsPage from 'components/nodes/NodeNews';
import { drupal } from 'lib/drupal';
import getMenus from 'lib/get-menus';
import { getParams } from 'lib/get-params';
import { GetStaticPathsResult, GetStaticPropsResult } from 'next';
import { DrupalMenuLinkContent, DrupalNode } from 'next-drupal';
import { ReactElement } from 'react';

const ENTITY_TYPES = ['node--news'];

interface NewsPageProps {
  entity: DrupalNode;
  menus: {
    main: DrupalMenuLinkContent[];
    footer: DrupalMenuLinkContent[];
    stickyShop: DrupalMenuLinkContent[];
  };
}

export default function NewsPage({ entity }: NewsPageProps) {
  if (!entity) return null;

  return <>{entity.type === 'node--news' && <NodeNewsPage node={entity} />}</>;
}

NewsPage.getLayout = (page: ReactElement) => {
  return <Layout {...page.props}>{page}</Layout>;
};

export async function getStaticPaths(context): Promise<GetStaticPathsResult> {
  // When this is true (in preview environments) don't
  // prerender any static pages
  // (faster builds, but slower initial page load)
  if (
    process.env.SKIP_BUILD_STATIC_GENERATION &&
    process.env.SKIP_BUILD_STATIC_GENERATION === 'true'
  ) {
    return {
      paths: [],
      fallback: 'blocking',
    };
  }
  return {
    paths: await drupal.getStaticPathsFromContext(ENTITY_TYPES, context, {
      pathPrefix: 'news',
    }),
    fallback: 'blocking',
  };
}

export async function getStaticProps(
  context
): Promise<GetStaticPropsResult<NewsPageProps>> {
  const path = await drupal.translatePathFromContext(context, {
    pathPrefix: 'news',
  });

  if (!path) {
    return {
      notFound: true,
    };
  }

  // Check for redirect.
  if (path.redirect?.length) {
    const [redirect] = path.redirect;
    return {
      redirect: {
        destination: redirect.to,
        permanent: redirect.status === '301',
      },
    };
  }

  const entity = await drupal.getResourceFromContext<DrupalNode>(
    path,
    context,
    {
      params: getParams(path.jsonapi.resourceName),
      withAuth: true,
    }
  );

  // If we're not in preview mode and the resource is not published,
  // Return page not found.
  if (!context.preview && entity?.status === false) {
    return {
      notFound: true,
    };
  }

  // Load additional ressources if needed.
  const menus = await getMenus(context);

  return {
    props: {
      entity,
      menus,
    },
    // Next.js will attempt to re-generate the page:
    // - When a request comes in
    // - At most once every 10 seconds
    revalidate: 10, // In seconds
  };
}
